import React from 'react';

/**
 * Modal for searching and selecting additional loan officers or processors from SoftPro
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {string} props.type - 'loanOfficer' or 'processor'
 * @param {string} props.searchTerm - Current search term
 * @param {Array} props.searchResults - Array of search results
 * @param {boolean} props.isSearching - Whether a search is in progress
 * @param {string} props.error - Error message if any
 * @param {Function} props.onSearchTermChange - Handler for search term changes
 * @param {Function} props.onSearch - Handler for search action
 * @param {Function} props.onSelect - Handler for selecting a person
 * @param {Function} props.onClose - Handler for closing the modal
 */
function ContactSearchModal({
  isOpen,
  type,
  searchTerm,
  searchResults,
  isSearching,
  error,
  onSearchTermChange,
  onSearch,
  onSelect,
  onClose
}) {
  if (!isOpen) return null;

  const typeLabel = type === 'loanOfficer' ? 'Loan Officer' : 'Processor';

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '30px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '1.5em' }}>
            Search for {typeLabel}
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 12px',
              backgroundColor: '#e5e7eb',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '1.2em',
              fontWeight: 'bold'
            }}
          >
            ✕
          </button>
        </div>

        <p style={{ color: '#6b7280', marginBottom: '20px' }}>
          Enter a name to search for in SoftPro:
        </p>

        <div style={{ marginBottom: '20px' }}>
          <input
            type="text"
            placeholder="Enter name (e.g., 'John Smith' or 'Smith')"
            value={searchTerm}
            onChange={(e) => onSearchTermChange(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                onSearch();
              }
            }}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '1em',
              border: '2px solid #d1d5db',
              borderRadius: '8px',
              outline: 'none'
            }}
            autoFocus
          />
          <button
            type="button"
            onClick={onSearch}
            disabled={isSearching}
            style={{
              marginTop: '10px',
              padding: '12px 24px',
              backgroundColor: isSearching ? '#9ca3af' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: isSearching ? 'not-allowed' : 'pointer',
              fontSize: '1em',
              fontWeight: '600',
              width: '100%'
            }}
          >
            {isSearching ? 'Searching...' : 'Search'}
          </button>
        </div>

        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#fee2e2',
            border: '1px solid #fca5a5',
            borderRadius: '8px',
            color: '#991b1b',
            marginBottom: '15px'
          }}>
            {error}
          </div>
        )}

        {searchResults.length > 0 && (
          <div>
            <p style={{ fontWeight: '600', marginBottom: '10px' }}>
              Found {searchResults.length} result(s):
            </p>
            <div style={{ maxHeight: '300px', overflow: 'auto' }}>
              {searchResults.map((person, idx) => (
                <div
                  key={idx}
                  onClick={() => onSelect(person)}
                  style={{
                    padding: '15px',
                    backgroundColor: '#f9fafb',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    marginBottom: '10px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e0f2fe';
                    e.currentTarget.style.borderColor = '#3b82f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  <div style={{ fontWeight: 'bold', fontSize: '1.1em', marginBottom: '6px' }}>
                    {person.FullName}
                  </div>
                  {person.Email && (
                    <div style={{ fontSize: '0.9em', color: '#6b7280', marginBottom: '4px' }}>
                      Email: {person.Email}
                    </div>
                  )}
                  {person.LookupCode && (
                    <div style={{ fontSize: '0.85em', color: '#9ca3af' }}>
                      Lookup Code: {person.LookupCode}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ContactSearchModal;
